import type {
  PimsFailureIncident,
  PimsReplayChapter,
} from "@pims/pimsAuditClient";
import { Button, cn, Swipe } from "@scribenote/brian-design-system";
import { useRef, useState } from "react";

/**
 * The native video player for a PIMS session replay: a plain
 * `<video controls preload="metadata">` — the browser's own controls are enough — plus
 * a vertical step list for navigation and a thin failure "gap map" rail beneath.
 *
 * Clicking a step seeks and plays. A failed step is marked in stopped ink on the rail and
 * its list entry carries an accessible "Failed" word plus its reason, visible without
 * pressing play. When the backend flags a `failedChapterIndex` the player opens parked at
 * that offset with the step highlighted, and the rail grows a "Show where I stopped"
 * affordance.
 */
export const PimsReplayPlayer = ({
  videoUrl,
  chapters,
  durationMs,
  failedChapterIndex = null,
  incidents = [],
  causalStepId = null,
  onVideoError,
}: {
  videoUrl: string;
  chapters: PimsReplayChapter[];
  durationMs: number | null;
  /** The `index` of the chapter the backend flagged as the write's failure
   * point, or null. Drives the on-load seek + initial highlight and the
   * rail's "Show where I stopped" control. */
  failedChapterIndex?: number | null;
  /** Clinic-safe failure incidents (WEB-3272). Drive the per-step rail
   * treatments (retry/recovered/contributing/terminal) via their `stepId`
   * join. Empty for old audits / v1 spines, which keep the legacy binary
   * stopped-ink treatment. */
  incidents?: PimsFailureIncident[];
  /** The `stepId` of the step the backend flagged as the terminal cause, or
   * null. Its chapter gets the "I stopped here" marker. */
  causalStepId?: number | null;
  /** Fired when the presigned `videoUrl` fails to load (e.g. expired) —
   * mirrors the screenshot audit's `onImageError`, so the caller can refetch
   * a fresh evidence payload with re-signed URLs. */
  onVideoError?: () => void;
}) => {
  const treatmentOf = buildTreatments(incidents, causalStepId);
  const failedChapter =
    failedChapterIndex != null
      ? (chapters.find((c) => c.index === failedChapterIndex) ?? null)
      : null;
  // `activeIndex` (and the `aria-current` it drives on the step entries) is
  // click-driven only, by design for this POC — it doesn't track native
  // `timeupdate` to highlight whichever chapter the playhead is currently in.
  // Deferred: playback-position-driven highlighting. It starts on the failed
  // chapter (when there is one) so the highlight matches the on-load seek.
  const [activeIndex, setActiveIndex] = useState<number | null>(
    failedChapter?.index ?? null,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  // The on-load seek happens exactly once. A presigned-URL refetch swaps
  // `src` and refires `loadedmetadata`; without this guard that would yank
  // the playhead back to the failure mid-viewing.
  const initialSeekDone = useRef(false);

  const seekTo = (chapter: PimsReplayChapter) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = chapter.startedMs / 1000;
      // Autoplay policy (or a load aborted mid-play) rejects the promise;
      // the seek has already landed either way, so swallow it rather than
      // logging an unhandled rejection.
      video.play().catch(() => {});
    }
    setActiveIndex(chapter.index);
  };

  // Seeking needs the media's metadata (duration) — a mount-time
  // `currentTime` write on an unloaded element is discarded — so the on-load
  // positioning waits for `loadedmetadata`. Paused on purpose: staff land on
  // the failure, they don't get surprise playback.
  const onLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !failedChapter || initialSeekDone.current) return;
    initialSeekDone.current = true;
    video.currentTime = failedChapter.startedMs / 1000;
  };

  return (
    <div
      className="flex flex-col gap-3 md:flex-row"
      data-testid="pims-replay-player"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* The recording letterboxes against the desk rather than raw black:
            the frame is a paper edge, the field behind it is the espresso the
            whole product sits on. */}
        {/* `data-dd-privacy="hidden"`: Session Replay's `mask` level censors
            `src` on IMG and SOURCE but not on VIDEO, so without this the
            presigned recording URL would be serialized verbatim into replay
            snapshots (WEB-3316). Not solved by moving `src` to a <source>
            child, because a source's load error does not reach `onError`
            here. */}
        <video
          ref={videoRef}
          src={videoUrl}
          data-dd-privacy="hidden"
          controls
          preload="metadata"
          onLoadedMetadata={onLoadedMetadata}
          onError={onVideoError}
          aria-label="The PIMS screen while I worked"
          className="max-h-[60vh] w-full rounded-paper border border-rule bg-desk object-contain"
          data-testid="pims-replay-video"
        />
        <ChapterRail
          chapters={chapters}
          durationMs={durationMs}
          failedChapter={failedChapter}
          treatmentOf={treatmentOf}
          onJumpToFailure={seekTo}
        />
      </div>

      {chapters.length > 0 && (
        <div className="flex min-w-0 flex-col gap-1 md:w-52 md:shrink-0">
          <span className="px-1 text-annotation-caps text-annotation">
            Steps
          </span>
          {chapters.map((chapter) => (
            <ChapterStep
              key={chapter.index}
              chapter={chapter}
              treatment={treatmentOf(chapter)}
              isActive={activeIndex === chapter.index}
              onSelect={() => seekTo(chapter)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** `12500` → `"0:12"`. Floors to whole seconds — a chapter's start offset is a
 * scrub target, not a precise duration. */
const formatOffset = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/** How a chapter's step reads against the failure incidents (WEB-3272):
 * `terminal` — the flagged causal step; `contributing` — a failure that fed the
 * terminal one; `retry` — a step that failed but was recovered; `recovered` —
 * the successful retry that recovered it; `failed` — the legacy binary stopped
 * ink for old audits / v1 spines with no incidents; `none` — a clean step. */
type StepTreatment =
  | "terminal"
  | "contributing"
  | "retry"
  | "recovered"
  | "failed"
  | "none";

/** Resolve each chapter to its rail treatment by joining incidents on
 * `stepId`. The causal step wins outright; otherwise a step's `impact` maps
 * directly (a `recovered` failure reads as `retry`), and a step that's some
 * incident's successful retry reads as `recovered`. Falls back to the legacy
 * binary `failed` only when there are no incidents at all, so old audits and
 * v1 spines (which never carry `stepId`) keep their original treatment. */
function buildTreatments(
  incidents: PimsFailureIncident[],
  causalStepId: number | null,
) {
  const failed = new Map<number, PimsFailureIncident["impact"]>();
  const retries = new Set<number>();
  for (const inc of incidents) {
    for (const s of inc.failedStepIds) failed.set(s, inc.impact);
    if (inc.successfulRetryStepId != null)
      retries.add(inc.successfulRetryStepId);
  }
  return (ch: PimsReplayChapter): StepTreatment => {
    if (ch.stepId != null) {
      if (causalStepId != null && ch.stepId === causalStepId) return "terminal";
      const impact = failed.get(ch.stepId);
      if (impact === "recovered") return "retry";
      if (impact) return impact; // "contributing" | "terminal"
      if (retries.has(ch.stepId)) return "recovered";
    }
    if (incidents.length === 0 && ch.outcome === "failed") return "failed";
    return "none";
  };
}

/** Marker ink on the rail: status ink standing alone, which is what `ink-*` is
 * for — the rail is a diagram, not a word, so it takes no wash. */
const MARKER_INK: Record<StepTreatment, string> = {
  terminal: "bg-ink-stopped",
  contributing: "bg-ink-stopped",
  failed: "bg-ink-stopped",
  retry: "bg-ink-flag",
  recovered: "bg-transparent",
  none: "bg-transparent",
};

/** A thin rail spanning the video's duration, with an absolute marker over
 * every failed chapter's start offset — the "gap map" staff scan before ever
 * pressing play. Renders nothing without a known duration (can't position
 * offsets as percentages of an unknown span). The rail itself is purely
 * decorative — `aria-hidden`, since every failed chapter's marker duplicates
 * information already exposed accessibly on its step entry (the "Failed"
 * word + error reason) — but when the backend flagged the failure point it
 * carries a real "Show where I stopped" button (outside the `aria-hidden` span)
 * that seeks-and-plays that chapter, same as clicking its step entry. */
const ChapterRail = ({
  chapters,
  durationMs,
  failedChapter,
  treatmentOf,
  onJumpToFailure,
}: {
  chapters: PimsReplayChapter[];
  durationMs: number | null;
  failedChapter: PimsReplayChapter | null;
  treatmentOf: (chapter: PimsReplayChapter) => StepTreatment;
  onJumpToFailure: (chapter: PimsReplayChapter) => void;
}) => {
  if (!durationMs || durationMs <= 0) return null;
  // Mark every problem step — a recovered attempt (flag ink) as well as a real
  // failure (stopped ink). A `recovered` step (the successful retry itself) and
  // clean steps carry no marker.
  const marked = chapters
    .map((chapter) => ({ chapter, treatment: treatmentOf(chapter) }))
    .filter(
      ({ treatment }) => treatment !== "none" && treatment !== "recovered",
    );
  if (marked.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      {/* A groove cut into the sheet, with the gaps scored across it. */}
      <div
        className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-recess"
        aria-hidden="true"
        data-testid="pims-replay-rail"
      >
        {marked.map(({ chapter, treatment }) => (
          <span
            key={chapter.index}
            className={cn("absolute top-0 h-full w-0.5", MARKER_INK[treatment])}
            style={{ left: `${(chapter.startedMs / durationMs) * 100}%` }}
            data-testid={`pims-replay-marker-${chapter.index}`}
          />
        ))}
      </div>
      {failedChapter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onJumpToFailure(failedChapter)}
          className="shrink-0 text-xs text-ink-stopped"
          data-testid="pims-replay-jump-to-failure"
        >
          Show where I stopped
        </Button>
      )}
    </div>
  );
};

/** Border/ink of a step entry keyed by its treatment. Retry reads in flag ink,
 * every real-failure treatment (contributing/terminal/legacy) in stopped ink,
 * and recovered/clean steps stay in plain annotation ink (the marked
 * "Recovered" word carries the signal on its own). Hover on a quiet step draws
 * its edge in rather than recolouring it. */
const STEP_TINT: Record<StepTreatment, string> = {
  terminal: "border-ink-stopped/60 text-ink-stopped",
  contributing: "border-ink-stopped/60 text-ink-stopped",
  failed: "border-ink-stopped/60 text-ink-stopped",
  retry: "border-ink-flag/60 text-ink-flag",
  recovered:
    "border-transparent text-annotation hover:border-rule hover:text-ink",
  none: "border-transparent text-annotation hover:border-rule hover:text-ink",
};

/** The word a treatment says, and the highlighter it wears. `none` and
 * `recovered`'s clean neighbours say nothing: a step that just worked needs no
 * mark. Every word here is a state, so every one of them is a Swipe. */
const STEP_WORD: Partial<
  Record<StepTreatment, { label: string; tone: "flag" | "working" | "stopped" }>
> = {
  retry: { label: "Retry", tone: "flag" },
  recovered: { label: "Recovered", tone: "working" },
  contributing: { label: "Failed", tone: "stopped" },
  terminal: { label: "I stopped here", tone: "stopped" },
  failed: { label: "Failed", tone: "stopped" },
};

const ChapterStep = ({
  chapter,
  treatment,
  isActive,
  onSelect,
}: {
  chapter: PimsReplayChapter;
  treatment: StepTreatment;
  isActive: boolean;
  onSelect: () => void;
}) => {
  // The error reason only reads as an error next to a stopped-ink treatment; a
  // recovered/retry step's error was already handled, so it stays quiet.
  const showError =
    treatment === "terminal" ||
    treatment === "contributing" ||
    treatment === "failed";
  const word = STEP_WORD[treatment];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isActive ? "true" : undefined}
      // Full-bleed row button with its focus ring drawn inset, so the ring
      // stays inside the step column instead of overlapping its neighbours.
      className={cn(
        "flex w-full min-w-0 cursor-pointer items-start gap-2 rounded-paper border px-2.5 py-1.5 text-left text-xs",
        "transition duration-150 ease-paper motion-reduce:transition-none",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        STEP_TINT[treatment],
        isActive ? "bg-recess" : "bg-transparent",
      )}
      data-testid={`pims-replay-chapter-${chapter.index}`}
    >
      {/* The offset is a filing line: mono, tabular, so the column of times
          reads straight down. */}
      <span className="mt-px w-8 shrink-0 font-mono text-[11px] tabular-nums text-annotation">
        {formatOffset(chapter.startedMs)}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        {/* Step labels are the agent's own wording, so unbounded: the line wraps
            rather than widening the column past the pane. */}
        <span className="flex min-w-0 flex-wrap items-center gap-1.5 font-medium [overflow-wrap:anywhere]">
          {chapter.label}
          {word && (
            <Swipe
              tone={word.tone}
              className="text-annotation-caps"
              data-testid={`pims-replay-chapter-${treatment}-${chapter.index}`}
            >
              {word.label}
            </Swipe>
          )}
        </span>
        {showError && chapter.error && (
          <span
            className="text-[11px] text-ink-stopped [overflow-wrap:anywhere]"
            data-testid={`pims-replay-chapter-error-${chapter.index}`}
          >
            {chapter.error}
          </span>
        )}
      </span>
    </button>
  );
};
